// Contact-page behaviour for the scan landing page (ContactMode).
// The TAP ITSELF is not here: decoding the base64 href and navigating is a small inline script on the
// page (Scans/Index.cshtml), so Call/Text/Email work even if this file never arrives (P1-010). This file
// is everything that is nice to have, and nothing in it may stand between a visitor and their call.
// Written in ES2017 syntax — no optional chaining — so it parses on older iPhones rather than failing whole.
// Jobs:
//   1. Record a LinkTapped interaction when the inline script reports a tap (window.syvContactTap).
//   2. Offer an optional, non-blocking location share that feeds the owner's "where are our vans
//      getting noticed" heatmap. The contact page is the destination, so this never redirects.
//   3. tel:/sms:/mailto: open a native app *over* this page rather than unloading it — so when the
//      visitor returns to the tab we surface a friendly one-time location reminder, caught at a
//      natural moment with zero friction on the contact action itself. (Website/social taps navigate
//      away, so no reminder fires there.)
document.addEventListener("DOMContentLoaded", () => {
    const form = document.getElementById("qr-contact-form");
    if (!form) return;

    const valueOf = (el) => (el && el.value) || "";

    const nanoId       = valueOf(form.querySelector("input[name='NanoId']"));
    const token        = valueOf(form.querySelector("input[name='__RequestVerificationToken']"));
    const scanPublicId = valueOf(document.getElementById("scan-public-id")) || "00000000-0000-0000-0000-000000000000";

    // Without these nothing can be POSTed — so nothing is measured and the location share is withdrawn.
    // The contact buttons are unaffected: they do not live here.
    const canPost = !!(nanoId && token);

    const locBtn      = document.getElementById("btn-share-location-contact");
    const locThanks   = document.getElementById("contact-location-thanks");
    const locDeclined = document.getElementById("contact-location-declined");
    const locReminder = document.getElementById("contact-location-reminder");
    const locDemoLink = document.getElementById("contact-location-demo-link"); // demo scans only

    let locationShared         = false;
    let pendingLocationReminder = false;

    // keepalive lets the POST outlive the navigation a tap triggers (sms:/https unload the page).
    const post = (payload) =>
        !canPost || typeof fetch !== "function" ? Promise.resolve() :
        fetch(window.location.href, {
            method: "POST",
            keepalive: true,
            headers: {
                "Content-Type": "application/json",
                "RequestVerificationToken": token
            },
            body: JSON.stringify(payload)
        });

    // ── Contact action taps ────────────────────────────────────────────────
    // Called by the inline script on the page just before it navigates. Must not throw and must not wait.
    window.syvContactTap = (target, href) => {
        // Measure the tap (fire-and-forget) — navigation never waits on the POST.
        post({ nanoId, scanPublicId, interactionType: 3 /* LinkTapped */, target, showPreview: false })
            .catch(() => {});

        // tel:/sms:/mailto: keep this page alive (a native app opens over it), so arm the
        // location reminder for when the visitor comes back. http(s) taps leave the page.
        if (/^(tel:|sms:|mailto:)/i.test(href) && !locationShared && locBtn && canPost && navigator.geolocation)
            pendingLocationReminder = true;
    };

    // ── Location share ─────────────────────────────────────────────────────
    // accepted=false (declined/failed) gets a neutral "No problem." — never nag, and never thank
    // someone for a share that didn't happen. The demo-only "see what was recorded" link only
    // appears when a location actually got recorded, since there's nothing new to show otherwise.
    const showThanks = (accepted) => {
        locationShared = true;
        pendingLocationReminder = false;
        if (locReminder) locReminder.classList.add("hidden");
        if (locBtn) { locBtn.classList.remove("animate-pulse"); locBtn.classList.add("hidden"); }
        const ack = accepted ? locThanks : locDeclined;
        if (ack) ack.classList.remove("hidden");
        if (accepted && locDemoLink) locDemoLink.classList.remove("hidden");
    };

    if (locBtn && canPost && navigator.geolocation) {
        locBtn.addEventListener("click", () => {
            // They've engaged — clear the reminder styling.
            pendingLocationReminder = false;
            locBtn.classList.remove("animate-pulse");
            if (locReminder) locReminder.classList.add("hidden");

            locBtn.disabled = true;
            locBtn.textContent = "Getting location…";

            navigator.geolocation.getCurrentPosition(
                pos => {
                    post({
                        nanoId,
                        scanPublicId,
                        interactionType:  0, // LocationAccepted
                        latitude:         pos.coords.latitude,
                        longitude:        pos.coords.longitude,
                        accuracy:         pos.coords.accuracy,
                        platform:         (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform,
                        language:         navigator.language,
                        screenWidth:      screen.width,
                        screenHeight:     screen.height,
                        screenPixelRatio: window.devicePixelRatio,
                        referrer:         document.referrer || null,
                        showPreview:      false
                    })
                    // Thank them - and offer "see what was recorded" - only once the share has actually been
                    // SAVED. Saying thanks the moment the browser handed over a position meant a quick tap on that
                    // link reached a page that knew nothing of it: no location, and no thank-you code either.
                    // A save that fails gets the neutral "No problem." - never thanks for something not recorded.
                    // (The returned redirect is never followed - the contact page is the destination.)
                    .then(response => showThanks(!!response && response.ok))
                    .catch(() => showThanks(false));

                    locBtn.textContent = "Saving…";
                },
                () => showThanks(false),          // declined / failed — acknowledge, don't nag
                { timeout: 15000, enableHighAccuracy: true }
            );
        });
    } else if (locBtn) {
        // No geolocation support (or nothing to send it with) — hide the ask rather than offer a dead button.
        locBtn.classList.add("hidden");
    }

    // ── Return-focus reminder ──────────────────────────────────────────────
    // When the tab regains focus after a Call/Text/Email tap, highlight the location ask once.
    const surfaceReminder = () => {
        if (!pendingLocationReminder || locationShared) return;
        if (document.visibilityState !== "visible") return;
        pendingLocationReminder = false;
        if (locReminder) locReminder.classList.remove("hidden");
        if (locBtn) {
            locBtn.classList.add("animate-pulse");
            locBtn.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };
    document.addEventListener("visibilitychange", surfaceReminder);
    window.addEventListener("focus", surfaceReminder);
});
